import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Eye, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { DistributionProcessGrouped } from "@/types/distributionProcess";
import { formatDate } from "@/utils/format-date";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { DistributionDetailsModal } from "../DetailsModal";

interface TableDataProps extends DistributionProcessGrouped {}

export const TableData = ({
  dataApi,
  pageActions,
  isLoading,
}: {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: TypePageActions;
  isLoading: boolean;
}) => {
  const [selectedContestId, setSelectedContestId] = useState<number | null>(
    null
  );
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const handleViewDetails = (contestId: number) => {
    setSelectedContestId(contestId);
    setDetailsModalOpen(true);
  };

  const getProductsDisplay = (item: TableDataProps) => {
    if (item.total_products > 0) {
      return `${item.total_products} produto${
        item.total_products > 1 ? "s" : ""
      }`;
    }

    return "-";
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Concurso</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Destinatário</TableHead>
            <TableHead className="text-center">Quantidade</TableHead>
            <TableHead>Produtos</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                Carregando...
              </TableCell>
            </TableRow>
          ) : dataApi?.data.length > 0 ? (
            dataApi.data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{item.contest.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(item.contest.validation_date)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className="text-xs text-white border-0"
                    style={{ backgroundColor: item.lottery.color || "#000" }}
                  >
                    {item.lottery.name}
                  </Badge>
                </TableCell>
                <TableCell>
                  {item.seller ? (
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {item.seller.user.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {item.distributionType.name}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <span className="font-medium">{item.unity.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.distributionType.name}
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className="font-mono">
                    {item.total_quantity}
                  </Badge>
                </TableCell>
                <TableCell>{getProductsDisplay(item)}</TableCell>
                <TableCell className="text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={() => handleViewDetails(item.contest_id)}
                      variant="outline"
                      size="icon"
                      className="size-8"
                      title="Ver detalhes"
                    >
                      <Eye className="size-5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                Nenhuma distribuição encontrada
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3 mt-4">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {/* Modal de detalhes */}
      {selectedContestId && (
        <DistributionDetailsModal
          contestId={selectedContestId}
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedContestId(null);
          }}
        />
      )}
    </>
  );
};
